import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { useVideoRecorder } from '../../hooks/useVideoRecorder'
import { uploadVideo, type VideoUploadResponse } from '../../api/media'
import { useChatStore, Message, nextTempId } from '../../stores/chatStore'

interface VideoRecorderProps {
  onClose: () => void
  onSuccess?: (result: VideoUploadResponse) => void
}

export default function VideoRecorder({ onClose, onSuccess }: VideoRecorderProps) {
  const videoPreviewRef = useRef<HTMLVideoElement>(null)
  const recordedVideoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const {
    isRecording,
    isPending,
    videoBlob,
    previewUrl,
    startRecording,
    stopRecording,
    cancelRecording,
    error,
    streamRef,
  } = useVideoRecorder()

  // Store tempId for updating after upload succeeds
  const tempIdRef = useRef<number | null>(null)

  const mutation = useMutation({
    mutationFn: (blob: Blob) => uploadVideo(blob),
    onSuccess: (result) => {
      // Update message with real ID and 'sent' status
      // This runs even after component unmounts (React Query handles it)
      const tempId = tempIdRef.current
      if (tempId) {
        const messages = useChatStore.getState().messages
        const updatedMessages = messages.map((m) =>
          m.id === tempId
            ? {
                ...m,
                id: result.message_id,
                content: result.transcription || '',
                videoUrl: result.video_url || m.videoUrl,  // BUG-048: server URL > blob URL
                status: result.transcription ? 'seen' as const : 'sent' as const,
              }
            : m
        )
        useChatStore.getState().setMessages(updatedMessages)
        tempIdRef.current = null
      }
      // Notify parent callback
      onSuccess?.(result)
    },
    onError: () => {
      // Remove the optimistic "sending" message on upload failure
      // Prevents stuck "sending" state (BUG-041)
      const tempId = tempIdRef.current
      if (tempId) {
        const store = useChatStore.getState()
        store.setMessages(store.messages.filter((m) => m.id !== tempId))
      }
      tempIdRef.current = null
    },
  })

  // Connect stream to video preview when recording
  useEffect(() => {
    if (videoPreviewRef.current && streamRef.current && isRecording) {
      videoPreviewRef.current.srcObject = streamRef.current
    }
  }, [isRecording, streamRef])

  const handleUpload = () => {
    if (videoBlob && previewUrl) {
      // Create temporary message ID (negative to distinguish from server IDs)
      const tempId = nextTempId()
      tempIdRef.current = tempId // Store for onSuccess callback

      // Add optimistic message to chat immediately with 'sending' status
      const optimisticMessage: Message = {
        id: tempId,
        role: 'user',
        content: '', // Empty for now, transcription comes later
        mediaType: 'video',
        status: 'sending', // Hourglass icon
        videoUrl: previewUrl, // Local blob URL for preview
        createdAt: new Date().toISOString(),
      }
      useChatStore.getState().addMessage(optimisticMessage)

      // Start upload - onSuccess in useMutation will update the message
      mutation.mutate(videoBlob)

      // Notify parent (will be called even after unmount via onSuccess)
      // Note: onSuccess callback moves to useMutation definition

      // Close modal immediately (optimistic UI)
      onClose()
    }
  }

  const handleCancel = () => {
    cancelRecording()
    onClose()
  }

  const handleRetake = () => {
    cancelRecording()
    setIsPlaying(false)
  }

  // Toggle play/pause for recorded video
  const handleVideoClick = () => {
    if (recordedVideoRef.current) {
      if (isPlaying) {
        recordedVideoRef.current.pause()
      } else {
        recordedVideoRef.current.play()
      }
    }
  }

  // Get emotion display name in Russian
  const getEmotionDisplay = (emotion: string | undefined) => {
    const emotionMap: Record<string, string> = {
      happiness: 'радость',
      sadness: 'грусть',
      anger: 'гнев',
      fear: 'страх',
      disgust: 'отвращение',
      surprise: 'удивление',
      contempt: 'презрение',
      neutral: 'нейтральное',
    }
    return emotion ? emotionMap[emotion] || emotion : null
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden animate-fade-in max-h-[80vh] overflow-y-auto -translate-y-48">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-neutral-800">
              Видеосообщение
            </h2>
            <button
              onClick={handleCancel}
              className="p-1 text-neutral-400 hover:text-neutral-600 rounded-lg hover:bg-neutral-100"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Circular video preview area - matches chat display */}
          <div className="flex justify-center mb-4">
            <div className="relative">
              {/* Circular container */}
              <div
                className={clsx(
                  'w-48 h-48 rounded-full overflow-hidden border-4 bg-neutral-900',
                  isRecording ? 'border-error animate-pulse' : 'border-primary-500'
                )}
              >
                {/* Live preview during recording */}
                {(isRecording || isPending) && (
                  <video
                    ref={videoPreviewRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                )}

                {/* Recorded video preview - clickable to play/pause */}
                {previewUrl && !isRecording && (
                  <video
                    ref={recordedVideoRef}
                    src={previewUrl}
                    playsInline
                    loop
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={handleVideoClick}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => setIsPlaying(false)}
                  />
                )}

                {/* Initial state - start prompt */}
                {!isRecording && !isPending && !previewUrl && (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center text-white/70">
                      <svg className="w-12 h-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  </div>
                )}

                {/* Pending indicator */}
                {isPending && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                    <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>

              {/* Play overlay for recorded video */}
              {previewUrl && !isRecording && !isPlaying && (
                <button
                  onClick={handleVideoClick}
                  className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-full hover:bg-black/30 transition-colors"
                >
                  <svg
                    className="w-12 h-12 text-white drop-shadow-lg"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
              )}

              {/* Recording indicator - positioned at top */}
              {isRecording && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-error/90 text-white px-3 py-1 rounded-full text-sm shadow-lg">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  Запись...
                </div>
              )}
            </div>
          </div>

          {/* Emotion analysis result */}
          {mutation.isSuccess && mutation.data?.emotion_detected && (
            <div className="mb-4 p-3 bg-primary-50 rounded-xl text-sm">
              <div className="flex items-center gap-2">
                <span className="text-neutral-600">Эмоция:</span>
                <span className="font-medium text-neutral-800">
                  {getEmotionDisplay(mutation.data.emotion_detected)}
                </span>
                {mutation.data.emotion_confidence && (
                  <span className="text-neutral-500">
                    ({Math.round(mutation.data.emotion_confidence * 100)}%)
                  </span>
                )}
              </div>
              {mutation.data.facial_cues && mutation.data.facial_cues.length > 0 && (
                <p className="mt-1 text-xs text-neutral-500">
                  Мимика: {mutation.data.facial_cues.join(', ')}
                </p>
              )}
            </div>
          )}

          {/* Controls */}
          <div className="flex gap-3">
            {/* Initial state - start button */}
            {!isRecording && !isPending && !previewUrl && (
              <button
                onClick={startRecording}
                className={clsx(
                  'flex-1 py-3 px-4 rounded-xl font-medium',
                  'bg-error text-white hover:bg-error-dark',
                  'transition-colors flex items-center justify-center gap-2'
                )}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="8" />
                </svg>
                Начать запись
              </button>
            )}

            {/* Recording state - stop button */}
            {isRecording && (
              <button
                onClick={stopRecording}
                className={clsx(
                  'flex-1 py-3 px-4 rounded-xl font-medium',
                  'bg-neutral-800 text-white hover:bg-neutral-900',
                  'transition-colors flex items-center justify-center gap-2'
                )}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
                Остановить
              </button>
            )}

            {/* Preview state - retake and send buttons */}
            {previewUrl && !isRecording && (
              <>
                <button
                  onClick={handleRetake}
                  disabled={mutation.isPending}
                  className={clsx(
                    'flex-1 py-2.5 px-4 rounded-xl font-medium',
                    'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
                    'transition-colors',
                    mutation.isPending && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  Переснять
                </button>
                <button
                  onClick={handleUpload}
                  disabled={mutation.isPending}
                  className={clsx(
                    'flex-1 py-2.5 px-4 rounded-xl font-medium',
                    'bg-primary-500 text-white hover:bg-primary-600',
                    'transition-colors',
                    mutation.isPending && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {mutation.isPending ? 'Анализ...' : 'Отправить'}
                </button>
              </>
            )}
          </div>

          {/* Error messages */}
          {error && (
            <p className="mt-4 text-sm text-error text-center">
              {error}
            </p>
          )}

          {mutation.isError && (
            <p className="mt-4 text-sm text-error text-center">
              Ошибка загрузки. Попробуйте ещё раз.
            </p>
          )}

          {/* Info text */}
          <p className="mt-4 text-xs text-neutral-400 text-center">
            Запишите короткое видео, и терапевт проанализирует ваше эмоциональное состояние
          </p>
        </div>
      </div>
    </div>
  )
}
